import { useEffect, useRef, useState, type ReactNode } from "react";

/** Distance (px, après résistance) à tirer pour déclencher l'actualisation. */
const THRESHOLD = 64;
/** Hauteur gardée par l'indicateur pendant l'actualisation. */
const HOLD = 52;
/** Tirage maximal affiché. */
const MAX = 110;
/** Mouvement (px) avant de décider si le geste est un tirage vers le bas. */
const SLOP = 8;

/**
 * Le geste appartient à autre chose qu'à la page : un panneau ouvert (fiche
 * produit, choix de la date), une zone marquée `data-no-pull`, ou une liste
 * qui défile elle-même et n'est pas en haut.
 */
function blocked(target: EventTarget | null): boolean {
  if (document.querySelector('[role="dialog"]')) return true;
  let el = target instanceof Element ? target : null;
  if (el?.closest("[data-no-pull]")) return true;
  for (; el && el !== document.body; el = el.parentElement) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === "auto" || oy === "scroll") && el.scrollTop > 0) return true;
  }
  return false;
}

/**
 * « Tirer pour actualiser » (vue mobile) : en haut de la page, glisser vers le
 * bas puis relâcher lance `onRefresh` ; un cercle se remplit pendant le tirage
 * et tourne jusqu'à la fin de l'actualisation. Remplace le geste natif du
 * navigateur (rechargement de toute la page sur Android, absent dans l'app
 * installée sur iPhone).
 */
export function PullToRefresh({ onRefresh, children }: { onRefresh: () => Promise<unknown>; children: ReactNode }) {
  const [pull, setPull] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;

  useEffect(() => {
    let start: { x: number; y: number } | null = null;
    let current = 0;
    let busy = false;

    const onStart = (e: TouchEvent) => {
      start = !busy && e.touches.length === 1 && window.scrollY <= 0 && !blocked(e.target) ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
    };
    const onMove = (e: TouchEvent) => {
      if (!start) return;
      const dx = e.touches[0].clientX - start.x;
      const dy = e.touches[0].clientY - start.y;
      if (current === 0) {
        if (Math.abs(dx) + Math.abs(dy) < SLOP) return;
        // Vers le haut, en travers, ou page déjà défilée : défilement normal.
        if (dy <= 0 || Math.abs(dx) > dy || window.scrollY > 0) {
          start = null;
          return;
        }
      }
      e.preventDefault(); // pas de rebond ni de rechargement natifs pendant le tirage
      current = Math.min(MAX, Math.max(0, dy - SLOP) * 0.5);
      setPull(current);
      setDragging(true);
    };
    const onEnd = () => {
      if (!start) return;
      start = null;
      setDragging(false);
      const reached = current >= THRESHOLD;
      current = 0;
      if (!reached) {
        setPull(0);
        return;
      }
      busy = true;
      setRefreshing(true);
      setPull(HOLD);
      void refresh
        .current()
        .catch(() => {})
        .finally(() => {
          busy = false;
          setRefreshing(false);
          setPull(0);
        });
    };

    // Le geste natif (rechargement Android) ne doit pas doubler le nôtre.
    const root = document.documentElement;
    const before = root.style.overscrollBehaviorY;
    root.style.overscrollBehaviorY = "contain";
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    return () => {
      root.style.overscrollBehaviorY = before;
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  const progress = Math.min(1, pull / THRESHOLD);
  const transition = dragging ? "none" : "height 0.25s ease, transform 0.25s ease";
  return (
    <div className="relative">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-center overflow-hidden"
        style={{ height: pull, transition }}
      >
        <Ring progress={progress} spinning={refreshing} />
      </div>
      <span role="status" className="sr-only">
        {refreshing ? "Actualisation…" : ""}
      </span>
      {/* « none » au repos : une transformation ferait des éléments `fixed` de la page (panneaux) des enfants de ce bloc. */}
      <div style={{ transform: pull ? `translateY(${pull}px)` : "none", transition }}>{children}</div>
    </div>
  );
}

/** Cercle qui se remplit avec le tirage, puis tourne pendant l'actualisation. */
function Ring({ progress, spinning }: { progress: number; spinning: boolean }) {
  const r = 10;
  const c = 2 * Math.PI * r;
  const arc = spinning ? 0.7 : progress * 0.9;
  return (
    <svg
      width={28}
      height={28}
      viewBox="0 0 28 28"
      className={spinning ? "animate-spin" : undefined}
      style={{ opacity: spinning ? 1 : 0.35 + progress * 0.65, transform: spinning ? undefined : `rotate(${progress * 270}deg)` }}
      aria-hidden
    >
      <circle cx={14} cy={14} r={r} fill="none" stroke="var(--ios-fill)" strokeWidth={2.5} />
      <circle
        cx={14}
        cy={14}
        r={r}
        fill="none"
        stroke="var(--accent)"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray={`${arc * c} ${c}`}
        transform="rotate(-90 14 14)"
      />
    </svg>
  );
}
